import React, { Fragment } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import usePostsInfiniteQuery from '../../../Hooks/usePostsInfiniteQuery';
import PostCard from '../../DesignSystem/PostCard/PostCard';
import PostCardSkeleton from '../../DesignSystem/PostCard/PostCardSkeleton';
import ButtonLoadMore from '../../Posts/ButtonLoadMore';
import ButtonViewAll from './ButtonViewAll';
import { PostVisibilities } from '../../../Utils/Types';
import { Post } from '../../../Utils/Types/Posts';

interface ListOfCardsProps {
  trackingProps?: {
    category: string;
    name: string;
  };
  space: any;
  queryKey?: string;
  titleI18nKey?: string;
  titleComponent?: any;
  postsTypes?: string[];
  emptyState?: any;
  limit?: number;
  viewAllHref?: string;
  viewAllEnabled?: boolean;
  newTab?: boolean;
  extraParams?: any;
}

function ListOfCards({
  space,
  queryKey = 'infinite-query-posts',
  titleI18nKey,
  titleComponent = 'h2',
  postsTypes = [],
  emptyState,
  trackingProps,
  limit = 10,
  viewAllHref,
  viewAllEnabled,
  newTab,
  extraParams = {},
}: ListOfCardsProps) {
  const { pages, postsCount, paging, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    usePostsInfiniteQuery([queryKey, postsTypes.join('-')], {
      space,
      types: postsTypes,
      visibilities: [PostVisibilities.PUBLISHED],
      limit,
      sort: '-dates',
      extraParams,
    });
  const showSkeleton = isFetching && !postsCount;
  const showEmptyState = postsCount === 0 && !showSkeleton;

  if (showEmptyState && !emptyState) {
    return null;
  }

  return (
    <>
      {titleI18nKey && (
        <Typography component={titleComponent} variant="h4" align="center" sx={{ pb: 4 }}>
          <FormattedMessage id={titleI18nKey} values={{ count: showSkeleton ? '#' : postsCount }} />
        </Typography>
      )}

      {showSkeleton &&
        [0, 1, 2].map((i) => (
          <Fade in timeout={500} key={i}>
            <Box key={i} sx={{ mb: 3 }}>
              <PostCardSkeleton />
            </Box>
          </Fade>
        ))}

      {showEmptyState && <Box sx={{ mb: 3 }}>{emptyState}</Box>}

      {!showSkeleton &&
        pages.map(({ data }: any) => (
          <Fragment key={data.paging.page}>
            {data.data.map((post: Post) => (
              <Fade in timeout={500} key={post.id}>
                <Box key={post.id} sx={{ mb: 3 }}>
                  <PostCard
                    trackingProps={trackingProps}
                    post={post}
                    entity={space}
                    newTab={newTab}
                  />
                </Box>
              </Fade>
            ))}
          </Fragment>
        ))}

      {hasNextPage && (
        <Box sx={{ mt: 3 }}>
          <ButtonLoadMore
            onClick={() => fetchNextPage()}
            paging={paging}
            isFetching={isFetchingNextPage}
            variant="text"
          />
        </Box>
      )}

      {viewAllHref && viewAllEnabled && (
        <Box sx={{ mt: 3 }}>
          <ButtonViewAll href={viewAllHref} variant="text" />
        </Box>
      )}
    </>
  );
}

export default ListOfCards;
